<script setup>
import {computed, ref, watch, onMounted} from 'vue'
import {useStore} from 'vuex'
import {useRouter} from 'vue-router'
import { toTypedSchema } from '@vee-validate/zod'
import * as z from 'zod'
import { getPlaceDetails } from '@/utils/nominatim'
import { generateDisplayName } from '@/utils/user'
import { API, HOST } from '@/utils/http'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select"
import { flattenProjectsWithLevel } from '@/utils/projectTree'
import { Spinner } from '@/components/ui/spinner'
import MemberSearchInput from '@/components/member/MemberSearchInput.vue'
import MemberInviteList from '@/components/member/MemberInviteList.vue'
import MemberManualEntryForm from '@/components/member/MemberManualEntryForm.vue'

import { FeatureSelection } from '@/components/feature-selection'
import ColorPicker from '@/components/ui/ColorPicker.vue'
import EmojiPicker from '@/components/ui/EmojiPicker.vue'
import { autoSimplifyFeature } from '@/utils/geometry'
import { MapPinPlus } from 'lucide-vue-next'

const router = useRouter()

const props = defineProps({
  project: {
    type: Object,
    default: null
  },
  edit: {
    type: Boolean,
    default: false
  }
})

const formRef = ref(null)
const isRegion = ref(false)
const currentBoundary = ref(null)
const boundaryColor = ref('#0059ff')
const isSubmiting = ref(false)
const selectedGeometry = ref(null)
const searchAddedMembers = ref([])

const store = useStore()
const orgs = computed(() => store.getters['org/getOrgs'])
const flatedProjects = computed(() => store.getters['project/getFlatedProjects'])
const treeProjects = computed(() => flattenProjectsWithLevel(
  (flatedProjects.value || []).filter(i => i.organization_id === props.project?.organization_id)
))

const member = computed(() => store.getters['user/getMember'])
const orgName = computed(() => orgs.value.find(i => i.organization_id === props.project?.organization_id)?.organization_name)
const showParentField = computed(() =>
  !props.edit || (!!props.project.parent_id && !!flatedProjects.value?.find(i => i.project_id === props.project.parent_id))
)
const currentOrgId = computed(() => props.project?.organization_id)

const PROJECT_ROLES = computed(() => store.getters['access/roleChoices']('project'))
const defaultRoleId = computed(() => store.getters['access/defaultJoinRoleId'])

const onSearchSelect = (searchMember) => {
  const searchIds = searchAddedMembers.value.map(m => m.member_id)
  if (searchIds.includes(searchMember.member_id)) return

  searchAddedMembers.value.push({
    member_id: searchMember.member_id,
    display_name: searchMember.display_name || `${searchMember.first_name || ''} ${searchMember.last_name || ''}`.trim(),
    first_name: searchMember.first_name || '',
    last_name: searchMember.last_name || '',
    avatar_url: searchMember.avatar_url,
    email: searchMember.email,
    phone: searchMember.phone,
    role: defaultRoleId.value,
    verified: searchMember.verified,
  })
}

const removeSearchMember = (index) => {
  searchAddedMembers.value.splice(index, 1)
}

const updateSearchMemberRole = (index, role) => {
  searchAddedMembers.value[index].role = role
}

const showManualEntry = ref(false)
const manualRole = ref(defaultRoleId.value)
const manualInitialContact = ref('')

const onAddNew = (text) => {
  manualInitialContact.value = text
  manualRole.value = defaultRoleId.value
  showManualEntry.value = true
}

const onManualSubmit = (values, role) => {
  searchAddedMembers.value.push({
    member_id: null,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: values.phone,
    avatar_url: null,
    display_name: generateDisplayName(values.first_name, values.last_name),
    role,
  })

  showManualEntry.value = false
  manualRole.value = defaultRoleId.value
}

const onManualCancel = () => {
  showManualEntry.value = false
}

const NO_WORKFLOW = 'none'
const workflows = ref([])

const fetchWorkflows = async () => {
  try {
    const response = await API({ method: 'GET', url: `${HOST}/project-workflows` })
    workflows.value = response.data
  } catch (e) {
    workflows.value = []
  }
}

const isoToEmoji = (isoCode) => {
  const codePoints = isoCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

const defaultParentId = props.edit
  ? (props.project.parent_id ? props.project.parent_id.toString() : null)
  : (props.project.project_id ? props.project.project_id.toString() : null)
const schema = ref(z.object({
  name: z.string().min(3).max(255).default(props.edit ? props.project.project_name : ''),
  description: z.string().max(255).nullable().optional().default(props.edit ? props.project.project_desc : ''),
  orgId: z.string().default(props.project ? props.project.organization_id.toString() : null),
  parentId: z.string().nullable().optional().default(defaultParentId),
  country: z.string().nullable().default(null),
  emoji: z.string().optional().nullable().default(props.edit ? props.project.emoji : null),
  workflow_id: z.string().optional().nullable().default(props.edit && props.project.workflow_id ? String(props.project.workflow_id) : NO_WORKFLOW),
  placeDetails: z.object({
    place_id: z.number(),
    country_code: z.string(),
    localname: z.string(),
    osm_type: z.string().optional(),
    osm_id: z.number().optional(),
  }).optional().nullable().default(null),
}))

const onSubmit = async (values) => {
  isSubmiting.value = true
  try {
    if (!props.edit) {
      const newProject = await store.dispatch('project/createProjectRequest', {
        parentId: values.parentId && values.parentId !== '' && values.parentId !== 'none' ? values.parentId : null,
        name: values.name,
        description: values.description,
        orgId: values.orgId,
        country: values.country,
        ui: 'Default',
        workflow_id: values.workflow_id && values.workflow_id !== NO_WORKFLOW ? parseInt(values.workflow_id) : null,
        emoji: values.emoji || null,
        member: member.value
      })

      const newProjectId = newProject?.project?.project_id

      if (!newProjectId) {
        isSubmiting.value = false
        return
      }

      // Save boundary if location data is present
      if (isRegion.value && (values.placeDetails || selectedGeometry.value)) {
        await store.dispatch('boundary/createBoundary', {
          projectId: newProjectId,
          geometry: selectedGeometry.value?.geometry || null,
          color: boundaryColor.value,
          placeDetails: reshaped() ? null : values.placeDetails || null,
        })
      }

      for (const pMember of searchAddedMembers.value) {
        await store.dispatch('project/addInvite', {
          organization_id: values.orgId,
          inviter_id: member.value.member_id,
          project_id: newProjectId,
          role_id: pMember.role,
          first_name: pMember.first_name,
          last_name: pMember.last_name,
          email: pMember.email,
          phone: pMember.phone,
        })
      }
    } else {
      await store.dispatch('project/updateProject', {
        project_id: props.project.project_id,
        parentId: values.parentId && values.parentId !== '' && values.parentId !== 'none' ? values.parentId : null,
        name: values.name,
        description: values.description,
        orgId: values.orgId,
        country: values.country,
        ui: props.project.ui || 'Default',
        workflow_id: values.workflow_id && values.workflow_id !== NO_WORKFLOW ? parseInt(values.workflow_id) : null,
        emoji: values.emoji || null,
        member: member.value
      })

      // Save/update boundary
      if (isRegion.value && (values.placeDetails || selectedGeometry.value || currentBoundary.value)) {
        const locationChanged = selectedGeometry.value && !selectedGeometry.value.properties?.isBoundary
        const placeDetails = reshaped()
          ? null
          : locationChanged ? values.placeDetails : currentBoundary.value?.properties?.place_details
        await store.dispatch('boundary/updateBoundary', {
          projectId: props.project.project_id,
          geometry: selectedGeometry.value?.geometry || currentBoundary.value?.geometry || null,
          color: boundaryColor.value,
          placeDetails: placeDetails || null,
        })
      } else if (!isRegion.value && currentBoundary.value) {
        await store.dispatch('boundary/deleteBoundary', props.project.project_id)
      }
    }

    if (values.parentId && values.parentId !== '' && values.parentId !== 'none') {
      router.push({ name: 'ProjectHome', params: { project_id: values.parentId } })
    } else {
      router.push({ name: 'Dashboard' })
    }

    await store.dispatch('project/fetchProjects')
  } finally {
    isSubmiting.value = false
  }
}

// A shape edited on the map is a drawn location: whatever place it started as, it's resolved from the shape.
const reshaped = () => !!selectedGeometry.value?.properties?.reshaped

const onFeatureSelected = async (feature, setValuesFunc) => {
  if (!feature) return

  if (feature.properties?.isSearch && feature.properties?.osmType && feature.properties?.osmId) {
    const details = await getPlaceDetails(feature.properties.osmType, feature.properties.osmId)
    if (!details) return

    setValuesFunc({
      name: props.edit ? props.project.project_name : details.localname,
      emoji: isoToEmoji(details.country_code),
      placeDetails: {
        place_id: details.place_id,
        country_code: details.country_code,
        localname: details.localname,
        osm_type: feature.properties.osmType,
        osm_id: feature.properties.osmId,
      }
    })
  } else if (feature.properties?.isFromFile) {
    setValuesFunc({
      name: props.edit ? props.project.project_name : (feature.properties.name || ''),
      placeDetails: null
    })
  } else {
    // Drawn feature - no place details, just select the geometry
    setValuesFunc({ placeDetails: null })
  }
}

const addLocation = () => {
  isRegion.value = true
}

const removeLocation = () => {
  isRegion.value = false
  selectedGeometry.value = null
}

onMounted(async () => {
  fetchWorkflows()
  if (props.edit && props.project?.project_id) {
    const boundary = await store.dispatch('boundary/fetchProjectBoundary', props.project.project_id)
    if (boundary) {
      currentBoundary.value = boundary
      boundaryColor.value = boundary.properties?.color || '#0059ff'
      // Backward compat: pre-fill emoji from boundary country_code if project has no emoji yet
      if (!props.project.emoji && boundary.properties?.place_details?.country_code) {
        formRef.value?.setFieldValue('emoji', isoToEmoji(boundary.properties.place_details.country_code))
      }
      if (boundary.properties?.place_details || boundary.geometry) {
        isRegion.value = true
      }
      if (boundary.geometry) {
        selectedGeometry.value = autoSimplifyFeature({
          type: 'Feature',
          id: 'boundary-' + props.project.project_id,
          geometry: boundary.geometry,
          properties: {
            name: boundary.properties?.place_details?.localname || 'Project Boundary',
            isBoundary: true,
            color: boundaryColor.value,
          }
        })
      }
    }
  }
})

watch(() => props.project, (value) => {
  schema.value = schema.value.extend({
    orgId: z.string().default(value.organization_id.toString()),
    parentId: z.string().nullable().optional().default(value.project_id ? value.project_id.toString() : null),
  })
}, { deep: true })
</script>

<template>
  <Form
    ref="formRef"
    :key="project.project_id ? project.project_id : null"
    class="max-w-2xl divide-y divide-border"
    v-slot="{ setValues }"
    :validation-schema="toTypedSchema(schema)"
    @submit="onSubmit"
  >
  <section class="pb-6 space-y-4">
    <FormField v-slot="{ componentField }" name="name">
      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <div class="flex items-center gap-2">
            <FormField v-slot="{ value: emojiValue }" name="emoji">
              <EmojiPicker :modelValue="emojiValue" @update:modelValue="(v) => setValues({ emoji: v })" />
            </FormField>
            <Input v-bind="componentField" class="flex-1" placeholder="Project name" />
          </div>
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>

    <FormField v-slot="{ componentField }" name="description">
      <FormItem>
        <FormLabel>Description <span class="text-muted-foreground font-normal">(Optional)</span></FormLabel>
        <FormControl>
          <Textarea v-bind="componentField" rows="2" class="min-h-0 resize-none" placeholder="What is this project about?" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </section>

  <section class="py-6 space-y-3">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="text-sm font-semibold">Location</h3>
        <p class="text-xs text-muted-foreground">Where the project is on the map</p>
      </div>
      <div v-if="isRegion" class="flex items-center gap-2">
        <ColorPicker v-model="boundaryColor" />
        <Button type="button" variant="ghost" size="sm" class="text-muted-foreground" @click="removeLocation">
          Remove
        </Button>
      </div>
    </div>

    <FeatureSelection
      v-if="isRegion"
      v-model:selectedGeometry="selectedGeometry"
      :color="boundaryColor"
      :allowed-draw-modes="['polygon']"
      merge-file
      @select="(feature) => onFeatureSelected(feature, setValues)"
    />
    <Button v-else type="button" variant="outline" class="w-full border-dashed" @click="addLocation">
      <MapPinPlus class="w-4 h-4 mr-2" />
      Add location
    </Button>
  </section>

  <section class="py-6 space-y-4">
    <div>
      <h3 class="text-sm font-semibold">Organization</h3>
      <p class="text-xs text-muted-foreground">{{ orgName }}</p>
    </div>

    <div class="grid gap-4 sm:grid-cols-2">
      <FormField v-if="showParentField" v-slot="{ componentField }" name="parentId">
        <FormItem>
          <FormLabel>Parent project</FormLabel>
          <FormControl>
            <Select v-bind="componentField">
              <SelectTrigger>
                <SelectValue placeholder="No parent project" />
              </SelectTrigger>
              <SelectContent class="max-h-[300px]">
                <SelectGroup>
                  <SelectItem value="none">
                    <span class="text-muted-foreground">No parent project</span>
                  </SelectItem>
                  <SelectItem
                    v-for="p of treeProjects"
                    :key="p.project_id"
                    :value="`${p.project_id}`"
                    :style="{ paddingLeft: `${32 + p._level * 16}px` }"
                  >
                    {{p.project_name}}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField v-slot="{ componentField }" name="workflow_id">
        <FormItem>
          <FormLabel>Workflow</FormLabel>
          <FormControl>
            <Select v-bind="componentField">
              <SelectTrigger>
                <SelectValue placeholder="Select a workflow" />
              </SelectTrigger>
              <SelectContent class="max-h-[300px]">
                <SelectGroup>
                  <SelectItem :value="NO_WORKFLOW">
                    <span class="text-muted-foreground">Default</span>
                  </SelectItem>
                  <SelectItem
                    v-for="c of workflows"
                    :key="c.id"
                    :value="String(c.id)"
                  >
                    {{c.name}}
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </section>

  <section v-if="!edit" class="py-6 space-y-3">
    <div>
      <h3 class="text-sm font-semibold">Members</h3>
      <p class="text-xs text-muted-foreground">Invite people to work on this project</p>
    </div>

    <MemberSearchInput
      v-if="currentOrgId"
      :org-id="currentOrgId"
      placeholder="ex. Maria, john@acme.com, or +1234..."
      @select="onSearchSelect"
      @add-new="onAddNew"
    />

    <MemberManualEntryForm
      v-if="showManualEntry"
      v-model:role="manualRole"
      :roles="PROJECT_ROLES"
      :default-role="defaultRoleId"
      mode="emailOrPhone"
      :initial-contact="manualInitialContact"
      @submit="onManualSubmit"
      @cancel="onManualCancel"
    />

    <MemberInviteList
      :rows="searchAddedMembers"
      :roles="PROJECT_ROLES"
      @remove="removeSearchMember"
      @update:role="updateSearchMemberRole"
    />
  </section>

  <section v-if="$slots.default" class="py-6">
    <slot />
  </section>

  <div class="sticky bottom-0 flex items-center justify-end gap-2 bg-background py-4">
    <Button type="button" variant="ghost" @click="router.back()">Cancel</Button>
    <Button type="submit" :disabled="isSubmiting">
      <Spinner v-if="isSubmiting" class="w-4 h-4" />
      <span v-else>{{ edit ? 'Save' : 'Create' }}</span>
    </Button>
  </div>
</Form>
</template>
